import React, { useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { AHIcon, AHSeparator, DialogButton, ListItem } from "arkh";
import { EmployeeRolesAdminQuery } from "./__generated__/EmployeeRolesAdminQuery.graphql";
import { FlatList, Pressable, View } from "react-native";

import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { ActionFooter } from "@/src/components/ActionFooter";
import { keyExtractorId } from "@/src/common/keyExtractors";
import { ListItemEmployees } from "widgets/src/TodoListItem/TodoListItemFooter/ListItemEmployees";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { EmployeeRoleEditEmployeesDialog } from "./EmployeeRoleEdit/EmployeeRoleEditEmployeesDialog";
import { EmployeeRolesAdminAssignMutation } from "./__generated__/EmployeeRolesAdminAssignMutation.graphql";

type ParamList = {
  EmployeeRolesAdminMain: UserLoggedInNavigatorParamList["EmployeeRolesAdmin"];
};

type EmployeeRolesAdminScreenProps<T extends keyof ParamList> = {
  navigation: CompositeNavigationProp<
    UserLoggedInNavigatorNavigationProp<"EmployeeRolesAdmin">,
    NativeStackNavigationProp<ParamList, T>
  >;
  route: RouteProp<ParamList, T>;
};

const Stack = createNativeStackNavigator<ParamList>();

export const EmployeeRolesAdmin = withWebModal(EmployeeRolesAdminC);

function EmployeeRolesAdminC(
  props: UserLoggedInNavigatorScreenProps<"EmployeeRolesAdmin">
) {
  const intl_tokens = useIntlTokens();
  const getOpts = useNavigationHeaderOptions();
  const opts = getOpts({
    type: "CLOSE",
    title: intl_tokens.Lineup,
    onClose: () => {
      props.navigation.goBack();
    },
  });

  return (
    <Stack.Navigator initialRouteName="EmployeeRolesAdminMain">
      <Stack.Screen
        name="EmployeeRolesAdminMain"
        component={EmployeeRolesAdminMain}
        initialParams={props.route.params}
        options={{
          ...opts,
        }}
      />
    </Stack.Navigator>
  );
}

function EmployeeRolesAdminMain(
  props: EmployeeRolesAdminScreenProps<"EmployeeRolesAdminMain">
) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerEmployeeRolesAdmin {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerEmployeeRolesAdmin(
  props: EmployeeRolesAdminScreenProps<"EmployeeRolesAdminMain"> & {
    fetchKey: string;
  }
) {
  const { teamId } = props.route.params;

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const data = useLazyLoadQuery<EmployeeRolesAdminQuery>(
    graphql`
      query EmployeeRolesAdminQuery($teamId: ID!) {
        teamsByIds(ids: [$teamId]) {
          roles {
            id
            name
            employeeCount
            employees {
              id
              ...ListItemEmployees_employees
            }
          }
        }
      }
    `,
    { teamId },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [assignEmployees] = useMutation<EmployeeRolesAdminAssignMutation>(
    graphql`
      mutation EmployeeRolesAdminAssignMutation(
        $roleId: ID!
        $employeeIds: [ID!]!
      ) {
        assignEmployeesToRole(roleId: $roleId, employeeIds: $employeeIds) {
          role {
            id
            name
            employeeCount
            employees {
              id
              ...ListItemEmployees_employees
            }
          }
        }
      }
    `
  );

  const roles = [...(data.teamsByIds?.[0]?.roles ?? [])]
    .filter((role) => role != null)
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedRole = roles.find((r) => r.id === selectedRoleId);

  const intl_tokens = useIntlTokens();
  const renderItem = ({
    item,
  }: {
    item: (typeof roles)[number];
    index: number;
  }) => {
    return (
      <ListItem
        onPress={() => {
          setSelectedRoleId(item.id);
        }}
        right={
          <Pressable
            hitSlop={20}
            onPress={() => {
              props.navigation.navigate("EmployeeRoleEdit", {
                id: item.id,
              });
            }}
            style={({ pressed }) => ({
              opacity: pressed ? 0.7 : 1,
              padding: 8,
              margin: -8,
            })}
          >
            <AHIcon type="more-vertical" />
          </Pressable>
        }
        footer={
          item.employees.length === 0 ? (
            <View style={{ alignSelf: "flex-start" }}>
              <DialogButton
                mode="inactive"
                onPress={() => setSelectedRoleId(item.id)}
                indicatorIcon="user"
              >
                {intl_tokens.Assign}
              </DialogButton>
            </View>
          ) : (
            <ListItemEmployees
              employees={item.employees}
              effectiveMaxEmployees={4}
              nameDisplayMode="all"
              currentEmployeeId={undefined}
              highlightViewer={false}
            />
          )
        }
      >
        <ListItem.Title>{item.name}</ListItem.Title>
      </ListItem>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      <FlatList
        ItemSeparatorComponent={AHSeparator}
        ListFooterComponent={AHSeparator}
        data={roles}
        renderItem={renderItem}
        keyExtractor={keyExtractorId}
      />
      {roles.length < 5 && (
        <ActionFooter
          safeInsetBottom
          primary={
            <ActionFooter.Primary
              title={intl_tokens.Create}
              icon="add"
              onPress={() => {
                props.navigation.navigate("EmployeeRoleCreate", { teamId });
              }}
            />
          }
        />
      )}
      <EmployeeRoleEditEmployeesDialog
        visible={selectedRoleId !== null}
        onClose={() => setSelectedRoleId(null)}
        assignEmployeesIds={selectedRole?.employees.map((e) => e.id) ?? []}
        setAssignEmployeesIds={(employeeIds) => {
          if (selectedRoleId) {
            assignEmployees({
              variables: {
                roleId: selectedRoleId,
                employeeIds,
              },
            });
          }
        }}
        teamId={teamId}
        title={selectedRole?.name}
      />
    </View>
  );
}
